import React, { useEffect, useState, memo } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { HomeIcon, BookOpenIcon, UsersIcon, BarChartIcon, CalendarIcon, SettingsIcon, MenuIcon, XIcon, ChevronLeftIcon, PlusCircleIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, EditIcon, SaveIcon, TrendingUpIcon, FileTextIcon } from 'lucide-react';
interface Task {
  id: number;
  name: string;
  status: 'pending' | 'in-progress' | 'completed';
  dueDate: string;
  grade?: number;
  feedback?: string;
}
interface GradeEntry {
  id: number;
  date: string;
  category: string;
  score: number;
  feedback: string;
}
export const StudentGradesOverview = () => {
  const {
    studentId
  } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('teacherDashboardTheme');
    return savedMode === 'dark';
  });
  const [isAddingGrade, setIsAddingGrade] = useState(false);
  const [newGrade, setNewGrade] = useState({
    category: 'Performance',
    score: 85,
    feedback: ''
  });
  // Mock student data - in a real app, this would be fetched from your database
  const student = {
    id: parseInt(studentId || '1'),
    name: ['Emma Parker', 'Michael Chen', 'Sarah Johnson'][parseInt(studentId || '1') - 1] || 'Emma Parker',
    instrument: ['Piano', 'Guitar', 'Violin'][parseInt(studentId || '1') - 1] || 'Piano',
    level: 'Intermediate',
    status: 'Active',
    avatar: ['https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/68.jpg'][parseInt(studentId || '1') - 1] || 'https://randomuser.me/api/portraits/women/44.jpg',
    averageScore: [92, 88, 95][parseInt(studentId || '1') - 1] || 92,
    progressNotes: 'Emma has shown significant improvement in her finger placement and timing. She completed all assigned exercises and is progressing well with her current piece.'
  };
  // Mock tasks data
  const [tasks, setTasks] = useState<Task[]>([{
    id: 1,
    name: 'Complete scale exercises',
    status: 'completed',
    dueDate: '2023-10-15',
    grade: 95,
    feedback: 'Excellent work on the scales, very fluid transitions.'
  }, {
    id: 2,
    name: 'Practice chord progressions',
    status: 'in-progress',
    dueDate: '2023-10-22'
  }, {
    id: 3,
    name: 'Prepare for recital piece',
    status: 'pending',
    dueDate: '2023-11-05'
  }]);
  // Mock grades history
  const [gradesHistory, setGradesHistory] = useState<GradeEntry[]>([{
    id: 1,
    date: '2023-09-10',
    category: 'Technique',
    score: 88,
    feedback: 'Good finger positioning, work on consistent tempo.'
  }, {
    id: 2,
    date: '2023-09-24',
    category: 'Theory',
    score: 92,
    feedback: 'Strong understanding of key signatures and chord structures.'
  }, {
    id: 3,
    date: '2023-10-08',
    category: 'Performance',
    score: 90,
    feedback: 'Expressive playing, minor memory issues in middle section.'
  }]);
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Handle adding a new grade
  const handleAddGrade = () => {
    const newEntry: GradeEntry = {
      id: gradesHistory.length + 1,
      date: new Date().toISOString().split('T')[0],
      category: newGrade.category,
      score: newGrade.score,
      feedback: newGrade.feedback
    };
    setGradesHistory([newEntry, ...gradesHistory]);
    setIsAddingGrade(false);
    setNewGrade({
      category: 'Performance',
      score: 85,
      feedback: ''
    });
  };
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
      case 'in-progress':
        return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'pending':
        return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      default:
        return darkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-800';
    }
  };
  // Get score color based on value
  const getScoreColor = (score: number) => {
    if (score >= 90) {
      return darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800';
    } else if (score >= 75) {
      return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
    } else if (score >= 60) {
      return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
    } else {
      return darkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-800';
    }
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Listen for theme changes from the Navbar component
  useEffect(() => {
    const handleThemeChange = (event: any) => {
      setDarkMode(event.detail.darkMode);
    };
    window.addEventListener('themeChange', handleThemeChange);
    return () => {
      window.removeEventListener('themeChange', handleThemeChange);
    };
  }, []);
  return <div className={`w-full min-h-screen ${darkMode ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-7265">
      <Navbar data-id="element-7266" />
      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-7267">
        {/* Mobile sidebar toggle */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-3 rounded-full shadow-lg" data-id="element-7268">
          {isSidebarOpen ? <XIcon data-id="element-7269" /> : <MenuIcon data-id="element-7270" />}
        </button>
        {/* Left Sidebar */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${darkMode ? 'bg-navy-900/90 shadow-lg' : 'bg-white shadow-md'} z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-7271">
          <nav className="px-4 py-6" data-id="element-7272">
            <ul className="space-y-2" data-id="element-7273">
              <li data-id="element-7274">
                <Link to="/teacher-dashboard" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-7275">
                  <HomeIcon className="h-5 w-5 mr-3" data-id="element-7276" />
                  Dashboard
                </Link>
              </li>
              <li data-id="element-7277">
                <Link to="/my-students" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-7278">
                  <UsersIcon className="h-5 w-5 mr-3" data-id="element-7279" />
                  Students
                </Link>
              </li>
              <li data-id="element-7280">
                <Link to="/reports" className={`flex items-center px-4 py-3 ${darkMode ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50'} rounded-lg font-medium`} data-id="element-7281">
                  <BarChartIcon className="h-5 w-5 mr-3" data-id="element-7282" />
                  Reports
                </Link>
              </li>
              <li data-id="element-7283">
                <Link to="/my-availability" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-7284">
                  <CalendarIcon className="h-5 w-5 mr-3" data-id="element-7285" />
                  Calendar
                </Link>
              </li>
              <li data-id="element-7286">
                <Link to="/user-info" className={`flex items-center px-4 py-3 ${darkMode ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} data-id="element-7287">
                  <SettingsIcon className="h-5 w-5 mr-3" data-id="element-7288" />
                  Settings
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6`} data-id="element-7289">
          <div className="max-w-5xl mx-auto" data-id="element-7290">
            {/* Back Button */}
            <div className="mb-4" data-id="element-7291">
              <button onClick={() => navigate('/grades-management')} className={`inline-flex items-center ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} transition-colors`} data-id="element-7292">
                <ChevronLeftIcon className="h-5 w-5 mr-1" data-id="element-7293" />
                Back to All Grades
              </button>
            </div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-7294">
              <div data-id="element-7295">
                <h1 className={`text-3xl font-serif font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7296">
                  Student Grades
                </h1>
                <p className={`mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7297">
                  Detailed performance overview for {student.name}
                </p>
              </div>
              <button onClick={() => setIsAddingGrade(true)} className="mt-4 md:mt-0 bg-deep-pink hover:bg-burgundy text-white py-2 px-4 rounded-lg transition-colors flex items-center" data-id="element-7298">
                <PlusCircleIcon className="h-5 w-5 mr-2" data-id="element-7299" />
                Add New Grade
              </button>
            </div>
            {/* Student Info Card */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 mb-6 transition-colors duration-300`} data-id="element-7300">
              <div className="flex flex-col md:flex-row md:items-center" data-id="element-7301">
                <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6" data-id="element-7302">
                  <div className="h-20 w-20 rounded-full overflow-hidden bg-gradient-to-br from-deep-pink to-burgundy" data-id="element-7303">
                    <img src={student.avatar} alt={student.name} className="h-full w-full object-cover" onError={e => {
                    e.currentTarget.parentElement.innerHTML = `
                          <div class="h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-bold bg-gradient-to-br from-deep-pink to-burgundy">
                            ${student.name.charAt(0)}
                          </div>
                        `;
                  }} data-id="element-7304" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-7305">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-7306">
                    <div data-id="element-7307">
                      <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7308">
                        {student.name}
                      </h2>
                      <div className="flex items-center mt-1" data-id="element-7309">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-navy-900/70 text-gray-300' : 'bg-gray-100 text-gray-800'} mr-2`} data-id="element-7310">
                          {student.instrument}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-800'}`} data-id="element-7311">
                          {student.status}
                        </span>
                      </div>
                    </div>
                    <div className={`mt-4 md:mt-0 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} rounded-lg px-4 py-3 text-center`} data-id="element-7312">
                      <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7313">
                        Average Score
                      </div>
                      <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7314">
                        {student.averageScore}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Performance Chart */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 mb-6 transition-colors duration-300`} data-id="element-7315">
              <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7316">
                Performance Over Time
              </h3>
              <div className={`${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} rounded-lg p-4 flex items-center justify-center h-64`} data-id="element-7317">
                <div className="text-center" data-id="element-7318">
                  <TrendingUpIcon className={`h-12 w-12 mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7319" />
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7320">
                    Performance chart visualization would appear here
                  </p>
                </div>
              </div>
            </div>
            {/* Tasks and Assignments */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 mb-6 transition-colors duration-300`} data-id="element-7321">
              <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7322">
                Tasks and Assignments
              </h3>
              <div className="space-y-3" data-id="element-7323">
                {tasks.map(task => <div key={task.id} className={`p-4 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} rounded-lg`} data-id="element-7324">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-7325">
                      <div data-id="element-7326">
                        <div className="flex items-center" data-id="element-7327">
                          <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7328">
                            {task.name}
                          </h4>
                          <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`} data-id="element-7329">
                            {task.status === 'completed' && <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-7330" />}
                            {task.status === 'in-progress' && <ClockIcon className="h-3 w-3 mr-1" data-id="element-7331" />}
                            {task.status === 'pending' && <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-7332" />}
                            {task.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`} data-id="element-7333">
                          Due: {formatDate(task.dueDate)}
                        </p>
                      </div>
                      {task.grade && <div className={`mt-3 md:mt-0 inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium ${getScoreColor(task.grade)}`} data-id="element-7334">
                          Grade: {task.grade}%
                        </div>}
                    </div>
                    {task.feedback && <div className={`mt-3 p-3 rounded ${darkMode ? 'bg-navy-800/80' : 'bg-white'}`} data-id="element-7335">
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7336">
                          <span className="font-medium" data-id="element-7337">Feedback:</span>{' '}
                          {task.feedback}
                        </p>
                      </div>}
                  </div>)}
              </div>
            </div>
            {/* Grade History */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 mb-6 transition-colors duration-300`} data-id="element-7338">
              <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7339">
                Grade History
              </h3>
              {isAddingGrade ? <div className={`p-4 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} rounded-lg mb-4`} data-id="element-7340">
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7341">
                    Add New Grade
                  </h4>
                  <div className="space-y-4" data-id="element-7342">
                    <div data-id="element-7343">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7344">
                        Category
                      </label>
                      <select value={newGrade.category} onChange={e => setNewGrade({
                    ...newGrade,
                    category: e.target.value
                  })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} data-id="element-7345">
                        <option value="Performance" data-id="element-7346">Performance</option>
                        <option value="Technique" data-id="element-7347">Technique</option>
                        <option value="Theory" data-id="element-7348">Theory</option>
                        <option value="Sight Reading" data-id="element-7349">Sight Reading</option>
                        <option value="Homework" data-id="element-7350">Homework</option>
                        <option value="Exam" data-id="element-7351">Exam</option>
                      </select>
                    </div>
                    <div data-id="element-7352">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7353">
                        Score (0-100)
                      </label>
                      <input type="number" min="0" max="100" value={newGrade.score} onChange={e => setNewGrade({
                    ...newGrade,
                    score: parseInt(e.target.value) || 0
                  })} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} data-id="element-7354" />
                    </div>
                    <div data-id="element-7355">
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7356">
                        Feedback
                      </label>
                      <textarea value={newGrade.feedback} onChange={e => setNewGrade({
                    ...newGrade,
                    feedback: e.target.value
                  })} rows={3} className={`w-full px-3 py-2 rounded-lg ${darkMode ? 'bg-navy-900 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink`} placeholder="Provide feedback on the student's performance..." data-id="element-7357"></textarea>
                    </div>
                    <div className="flex justify-end space-x-3" data-id="element-7358">
                      <button onClick={() => setIsAddingGrade(false)} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`} data-id="element-7359">
                        Cancel
                      </button>
                      <button onClick={handleAddGrade} className="bg-deep-pink hover:bg-burgundy text-white px-4 py-2 rounded-lg transition-colors flex items-center" data-id="element-7360">
                        <SaveIcon className="h-4 w-4 mr-2" data-id="element-7361" />
                        Save Grade
                      </button>
                    </div>
                  </div>
                </div> : null}
              <div className="overflow-x-auto" data-id="element-7362">
                <table className={`min-w-full ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7363">
                  <thead data-id="element-7364">
                    <tr data-id="element-7365">
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-7366">
                        Date
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-7367">
                        Category
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-7368">
                        Score
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`} data-id="element-7369">
                        Feedback
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${darkMode ? 'divide-navy-700' : 'divide-gray-200'} divide-y`} data-id="element-7370">
                    {gradesHistory.map(grade => <tr key={grade.id} data-id="element-7371">
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7372">
                          {formatDate(grade.date)}
                        </td>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7373">
                          {grade.category}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap" data-id="element-7374">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColor(grade.score)}`} data-id="element-7375">
                            {grade.score}%
                          </span>
                        </td>
                        <td className={`px-4 py-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7376">
                          {grade.feedback}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Progress Notes */}
            <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 mb-6 transition-colors duration-300`} data-id="element-7377">
              <div className="flex justify-between items-center mb-4" data-id="element-7378">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-7379">
                  Progress Notes
                </h3>
                <button className={`${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-7380">
                  <EditIcon className="h-4 w-4" data-id="element-7381" />
                </button>
              </div>
              <div className={`p-4 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} rounded-lg`} data-id="element-7382">
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7383">
                  {student.progressNotes}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer data-id="element-7384" />
    </div>;
};